import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';
import { statsAPI, aboutAPI, businessesAPI, advertisersAPI, supportersAPI, eventsAPI } from '../../api';
import axios from 'axios';
import { BarChart3, BookOpen, Image as ImageIcon, Library, Loader2, RotateCcw, Wrench, ShieldCheck, Trash2, Plus } from 'lucide-react';
import MediaPicker from '../../components/MediaPicker';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SiteSettingsPage — homepage content controls, moved out of the old
 * monolithic dashboard: the four headline stats + the "Discover Paisley"
 * story block.
 */
const SiteSettingsPage = () => {
  const { toast } = useToast();

  const [siteStats, setSiteStats] = useState({ active_partners: '', events_yearly: '', monthly_visitors: '', satisfaction_rate: '' });
  const [statsSaving, setStatsSaving] = useState(false);
  const [statsRecomputing, setStatsRecomputing] = useState(false);

  const [aboutContent, setAboutContent] = useState({
    eyebrow: '', title: '', title_highlight: '', paragraph_1: '', paragraph_2: '',
    image_url: '', image_alt: '', benefits: ['', '', '', '', ''], visible: true,
  });
  const [aboutSaving, setAboutSaving] = useState(false);
  const [aboutMediaOpen, setAboutMediaOpen] = useState(false);

  // ----- Hero image override -----
  const [heroStatus, setHeroStatus] = useState(null); // {default_url, current_url, override_active, revert_at, ...}
  const [heroForm, setHeroForm] = useState({ url: '', hours: 48, caption: '' });
  const [heroBusy, setHeroBusy] = useState(false);
  const [heroMediaOpen, setHeroMediaOpen] = useState(false);

  // ----- Trusted organisations -----
  const [trusted, setTrusted] = useState([]);
  const [trustCandidates, setTrustCandidates] = useState([]);
  const [trustForm, setTrustForm] = useState({ email: '', email_domain: '', org_name: '', note: '' });
  const [trustBusy, setTrustBusy] = useState(false);

  const loadTrusted = () => {
    axios.get(`${API}/api/admin/trusted-orgs`, { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } })
      .then((r) => setTrusted(r.data.trusted || [])).catch(() => setTrusted([]));
    axios.get(`${API}/api/admin/trusted-orgs/candidates`, { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } })
      .then((r) => setTrustCandidates(r.data.candidates || [])).catch(() => setTrustCandidates([]));
  };

  const loadHeroStatus = () => {
    axios.get(`${API}/api/admin/hero-image/status`, { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } })
      .then((r) => setHeroStatus(r.data))
      .catch(() => setHeroStatus(null));
  };

  useEffect(() => {
    statsAPI.get().then((r) => {
      if (r.data) {
        setSiteStats({
          active_partners: r.data.active_partners || '',
          events_yearly: r.data.events_yearly || '',
          monthly_visitors: r.data.monthly_visitors || '',
          satisfaction_rate: r.data.satisfaction_rate || '',
        });
      }
    }).catch(() => {});
    aboutAPI.get().then((r) => {
      if (r.data) {
        const a = r.data;
        setAboutContent({
          eyebrow: a.eyebrow || '',
          title: a.title || '',
          title_highlight: a.title_highlight || '',
          paragraph_1: a.paragraph_1 || '',
          paragraph_2: a.paragraph_2 || '',
          image_url: a.image_url || '',
          image_alt: a.image_alt || '',
          benefits: Array.isArray(a.benefits) ? [...a.benefits, '', '', '', '', ''].slice(0, 5) : ['', '', '', '', ''],
          visible: a.visible !== false,
        });
      }
    }).catch(() => {});
    loadHeroStatus();
    loadTrusted();
  }, []);

  const saveSiteStats = async () => {
    setStatsSaving(true);
    try {
      await statsAPI.update(siteStats);
      toast({ title: 'Site stats updated', description: 'Homepage will reflect the new numbers immediately.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setStatsSaving(false);
    }
  };

  const recomputeFromData = async () => {
    setStatsRecomputing(true);
    try {
      const [biz, ads, sup, fp, evRes] = await Promise.all([
        businessesAPI.getAll(),
        advertisersAPI.getAll(),
        supportersAPI.getAll(),
        axios.get(`${API}/api/featured-partners`),
        eventsAPI.list({ limit: 500 }),
      ]);
      const totalPartners = biz.data.length + ads.data.length + sup.data.length + fp.data.length;
      const yearAhead = new Date();
      yearAhead.setMonth(yearAhead.getMonth() + 12);
      const futureEvents = (evRes.data || []).filter((e) => {
        const d = new Date(e.start_date);
        return d >= new Date() && d <= yearAhead;
      }).length;
      const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}K+` : `${n}+`);
      setSiteStats((s) => ({
        ...s,
        active_partners: totalPartners > 0 ? `${totalPartners}+` : s.active_partners,
        events_yearly: futureEvents > 0 ? fmt(futureEvents) : s.events_yearly,
      }));
      toast({
        title: 'Recomputed from data',
        description: `Partners → ${totalPartners}+. Events (next 12m) → ${futureEvents || '—'}. Click Save to apply.`,
      });
    } catch (e) {
      toast({ title: 'Recompute failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setStatsRecomputing(false);
    }
  };

  const saveAboutContent = async () => {
    setAboutSaving(true);
    try {
      const payload = {
        ...aboutContent,
        benefits: (aboutContent.benefits || []).map((b) => (b || '').trim()).filter(Boolean),
      };
      await aboutAPI.update(payload);
      toast({ title: 'Discover Paisley updated', description: 'Live on the homepage now.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setAboutSaving(false);
    }
  };

  const applyHeroOverride = async () => {
    if (!heroForm.url.trim()) {
      toast({ title: 'Add an image URL first', variant: 'destructive' });
      return;
    }
    setHeroBusy(true);
    try {
      const hours = Math.max(1, Math.min(90 * 24, Number(heroForm.hours) || 48));
      const revert_at = new Date(Date.now() + hours * 3600 * 1000).toISOString();
      await axios.post(
        `${API}/api/admin/hero-image`,
        { url: heroForm.url.trim(), revert_at, caption: heroForm.caption.trim() || null },
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } },
      );
      toast({ title: `Hero swapped`, description: `Will auto-revert in ${hours} hours.` });
      loadHeroStatus();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setHeroBusy(false);
    }
  };

  const revertHeroNow = async () => {
    setHeroBusy(true);
    try {
      await axios.delete(
        `${API}/api/admin/hero-image`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } },
      );
      toast({ title: 'Hero reverted to default.' });
      setHeroForm({ url: '', hours: 48, caption: '' });
      loadHeroStatus();
    } catch (e) {
      toast({ title: 'Revert failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setHeroBusy(false);
    }
  };

  const addTrusted = async () => {
    if (!trustForm.email.trim() && !trustForm.email_domain.trim()) {
      toast({ title: 'Provide an email or a domain', variant: 'destructive' });
      return;
    }
    setTrustBusy(true);
    try {
      await axios.post(
        `${API}/api/admin/trusted-orgs`,
        {
          email: trustForm.email.trim().toLowerCase() || undefined,
          email_domain: trustForm.email_domain.trim().toLowerCase().replace(/^@/, '') || undefined,
          org_name: trustForm.org_name.trim() || undefined,
          note: trustForm.note.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } },
      );
      toast({ title: 'Added to trusted list' });
      setTrustForm({ email: '', email_domain: '', org_name: '', note: '' });
      loadTrusted();
    } catch (e) {
      toast({ title: 'Add failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setTrustBusy(false); }
  };

  const removeTrusted = async (t) => {
    if (!window.confirm(`Remove trust for ${t.email || t.email_domain}?`)) return;
    try {
      await axios.delete(
        `${API}/api/admin/trusted-orgs/${t.id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } },
      );
      loadTrusted();
    } catch (e) {
      toast({ title: 'Remove failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const promoteCandidate = async (c) => {
    setTrustBusy(true);
    try {
      await axios.post(
        `${API}/api/admin/trusted-orgs`,
        { email: c.email, org_name: c.org || undefined, note: `Auto-promoted after ${c.count} events`, auto_added: true },
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } },
      );
      toast({ title: `Promoted ${c.email}` });
      loadTrusted();
    } catch (e) {
      toast({ title: 'Promote failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setTrustBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="site-settings-page">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Site Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Control the homepage headline numbers and the "Discover Paisley" story block.</p>
      </div>

      {/* Hero image override */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 mb-6" data-testid="hero-image-editor">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <ImageIcon size={18} className="text-sky-500" /> Homepage hero image
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Temporarily swap the big photo at the top of the homepage. Auto-reverts back to the default when the timer runs out — no need to remember.
            </p>
          </div>
          {heroStatus?.override_active && (
            <span className="text-xs bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-3 py-1 font-semibold" data-testid="hero-status-badge">
              Override active
            </span>
          )}
        </div>

        {heroStatus?.override_active && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-900" data-testid="hero-status-panel">
            <p className="font-semibold">Currently showing custom hero</p>
            <p className="text-xs text-amber-800 mt-1">
              Reverts at <span className="font-mono">{new Date(heroStatus.revert_at).toLocaleString('en-GB')}</span>
              {heroStatus.set_by ? <> · set by <span className="font-semibold">{heroStatus.set_by}</span></> : null}
            </p>
            <img src={heroStatus.current_url} alt="Current hero" className="mt-2 rounded-md max-h-32 object-cover" />
            <Button type="button" variant="outline" size="sm" onClick={revertHeroNow} disabled={heroBusy} className="mt-2 text-xs" data-testid="hero-revert-now-btn">
              <RotateCcw size={12} className="mr-1" />Revert to default now
            </Button>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">New hero image URL</label>
            <div className="flex gap-2 mt-1">
              <Input
                value={heroForm.url}
                onChange={(e) => setHeroForm((f) => ({ ...f, url: e.target.value }))}
                placeholder="https://…/image.jpg — paste a URL or pick from Media Library"
                data-testid="hero-url-input"
              />
              <Button type="button" variant="outline" onClick={() => setHeroMediaOpen(true)} data-testid="hero-pick-media-btn">
                <Library size={14} className="mr-1" />Pick
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Auto-revert after (hours)</label>
              <Input
                type="number" min={1} max={2160}
                value={heroForm.hours}
                onChange={(e) => setHeroForm((f) => ({ ...f, hours: e.target.value }))}
                className="mt-1"
                data-testid="hero-hours-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">Default 48h = 2 days. Cap: 2160h (90 days).</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Caption override (optional)</label>
              <Input
                value={heroForm.caption}
                onChange={(e) => setHeroForm((f) => ({ ...f, caption: e.target.value }))}
                placeholder="Leave blank to keep the default caption"
                className="mt-1"
                data-testid="hero-caption-input"
              />
            </div>
          </div>
          <Button
            type="button"
            onClick={applyHeroOverride}
            disabled={heroBusy || !heroForm.url.trim()}
            className="bg-sky-500 hover:bg-sky-400 w-full"
            data-testid="hero-apply-btn"
          >
            {heroBusy ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
            {heroBusy ? 'Saving…' : (heroStatus?.override_active ? 'Replace current override' : 'Swap hero temporarily')}
          </Button>
        </div>

        {heroMediaOpen && (
          <MediaPicker
            open={heroMediaOpen}
            onClose={() => setHeroMediaOpen(false)}
            onSelect={(u) => { setHeroForm((f) => ({ ...f, url: u })); setHeroMediaOpen(false); }}
          />
        )}
      </div>

      {/* Trusted organisations */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 mb-6" data-testid="trusted-orgs-panel">
        <div className="mb-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck size={18} className="text-emerald-500" /> Trusted organisations
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Community submissions from these emails or domains skip the draft queue and auto-publish. Paid events over £5/person are always held for review regardless of trust.
          </p>
        </div>

        {trustCandidates.length > 0 && (
          <div className="mb-4 bg-emerald-50/60 border border-emerald-200 rounded-lg p-3" data-testid="trust-candidates">
            <p className="text-xs font-bold text-emerald-900 mb-2">Candidates ready to promote — 3+ published events already</p>
            <div className="space-y-1.5">
              {trustCandidates.map((c) => (
                <div key={c.email} className="flex items-center justify-between bg-white border border-emerald-100 rounded px-3 py-1.5 text-sm">
                  <div>
                    <span className="font-semibold">{c.org || '—'}</span>
                    <span className="text-slate-500 ml-2 font-mono text-xs">{c.email}</span>
                    <span className="text-emerald-700 ml-2 text-xs">· {c.count} events</span>
                  </div>
                  <Button size="sm" variant="outline" disabled={trustBusy} onClick={() => promoteCandidate(c)} data-testid={`promote-${c.email}`}>Promote</Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-2 mb-3">
          <Input placeholder="events@example.org (specific email)" value={trustForm.email} onChange={(e) => setTrustForm((f) => ({ ...f, email: e.target.value }))} data-testid="trust-email-input" />
          <Input placeholder="example.org (whole domain)" value={trustForm.email_domain} onChange={(e) => setTrustForm((f) => ({ ...f, email_domain: e.target.value }))} data-testid="trust-domain-input" />
          <Input placeholder="Organisation name (optional)" value={trustForm.org_name} onChange={(e) => setTrustForm((f) => ({ ...f, org_name: e.target.value }))} data-testid="trust-name-input" />
          <Input placeholder="Internal note (optional)" value={trustForm.note} onChange={(e) => setTrustForm((f) => ({ ...f, note: e.target.value }))} data-testid="trust-note-input" />
        </div>
        <Button onClick={addTrusted} disabled={trustBusy} className="bg-emerald-500 hover:bg-emerald-600 w-full" data-testid="trust-add-btn">
          <Plus size={14} className="mr-2" />{trustBusy ? 'Saving…' : 'Add trusted org'}
        </Button>

        {trusted.length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-3" data-testid="trusted-list">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Currently trusted ({trusted.length})</p>
            <div className="space-y-1.5">
              {trusted.map((t) => (
                <div key={t.id} className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded px-3 py-1.5 text-sm">
                  <div>
                    <span className="font-semibold">{t.org_name || t.email || t.email_domain}</span>
                    <span className="text-slate-500 ml-2 font-mono text-xs">{t.email || `@${t.email_domain}`}</span>
                    {t.auto_added && <span className="ml-2 text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">auto-promoted</span>}
                    {t.note && <span className="text-slate-500 ml-2 text-xs italic">· {t.note}</span>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeTrusted(t)} data-testid={`trust-remove-${t.id}`}><Trash2 size={14} className="text-rose-500" /></Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Homepage stats */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 mb-6" data-testid="site-stats-editor">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 size={18} className="text-sky-500" /> Homepage Stats
          </h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={recomputeFromData}
            disabled={statsRecomputing}
            className="text-xs"
            data-testid="stats-recompute-btn"
          >
            {statsRecomputing ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Wrench size={12} className="mr-1.5" />}
            {statsRecomputing ? 'Recomputing…' : 'Recompute from data'}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          These are the four headline numbers under the hero on paisley.org.uk. Free-text strings — use suffixes like
          <code className="bg-slate-100 px-1 mx-1 rounded">+</code>,
          <code className="bg-slate-100 px-1 mx-1 rounded">K+</code>,
          <code className="bg-slate-100 px-1 mx-1 rounded">M+</code> or
          <code className="bg-slate-100 px-1 mx-1 rounded">%</code> for impact.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Active Partners</label>
            <Input
              value={siteStats.active_partners}
              onChange={(e) => setSiteStats((s) => ({ ...s, active_partners: e.target.value }))}
              placeholder="150+"
              className="mt-1"
              data-testid="stats-active-partners-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">Auto-derivable from businesses + advertisers + supporters.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Events Yearly</label>
            <Input
              value={siteStats.events_yearly}
              onChange={(e) => setSiteStats((s) => ({ ...s, events_yearly: e.target.value }))}
              placeholder="500+"
              className="mt-1"
              data-testid="stats-events-yearly-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">Auto-derivable from events in next 12 months.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Monthly Visitors</label>
            <Input
              value={siteStats.monthly_visitors}
              onChange={(e) => setSiteStats((s) => ({ ...s, monthly_visitors: e.target.value }))}
              placeholder="1.2M+"
              className="mt-1"
              data-testid="stats-monthly-visitors-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">Manual — read from Google Analytics dashboard.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Satisfaction Rate</label>
            <Input
              value={siteStats.satisfaction_rate}
              onChange={(e) => setSiteStats((s) => ({ ...s, satisfaction_rate: e.target.value }))}
              placeholder="98%"
              className="mt-1"
              data-testid="stats-satisfaction-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">Manual — editorial value.</p>
          </div>
        </div>
        <Button
          type="button"
          onClick={saveSiteStats}
          disabled={statsSaving}
          className="mt-4 bg-sky-500 hover:bg-sky-400 w-full"
          data-testid="stats-save-btn"
        >
          {statsSaving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
          {statsSaving ? 'Saving…' : 'Save stats'}
        </Button>
      </div>

      {/* Discover Paisley editor */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200" data-testid="about-editor">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <BookOpen size={18} className="text-sky-500" /> "Discover Paisley" block
          </h3>
          <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-600">
            <input
              type="checkbox"
              checked={aboutContent.visible}
              onChange={(e) => setAboutContent((c) => ({ ...c, visible: e.target.checked }))}
              className="w-4 h-4 rounded"
              data-testid="about-visible-toggle"
            />
            Show on homepage
          </label>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          The story block that sits between the news mosaic and upcoming events on paisley.org.uk. Anything you save here goes live immediately.
        </p>

        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Eyebrow tag</label>
            <Input
              value={aboutContent.eyebrow}
              onChange={(e) => setAboutContent((c) => ({ ...c, eyebrow: e.target.value }))}
              placeholder="About Paisley"
              className="mt-1"
              data-testid="about-eyebrow-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Highlight word in title</label>
            <Input
              value={aboutContent.title_highlight}
              onChange={(e) => setAboutContent((c) => ({ ...c, title_highlight: e.target.value }))}
              placeholder="Web"
              className="mt-1"
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Heading</label>
          <Input
            value={aboutContent.title}
            onChange={(e) => setAboutContent((c) => ({ ...c, title: e.target.value }))}
            placeholder="Paisley on the Web"
            className="mt-1"
            data-testid="about-title-input"
          />
        </div>

        <div className="mb-3">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Paragraph 1</label>
          <textarea
            value={aboutContent.paragraph_1}
            onChange={(e) => setAboutContent((c) => ({ ...c, paragraph_1: e.target.value }))}
            rows={3}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            data-testid="about-paragraph1-input"
          />
        </div>

        <div className="mb-3">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Paragraph 2</label>
          <textarea
            value={aboutContent.paragraph_2}
            onChange={(e) => setAboutContent((c) => ({ ...c, paragraph_2: e.target.value }))}
            rows={3}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>

        <div className="mb-3">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Bullet points (up to 5)</label>
          <div className="space-y-2 mt-1">
            {aboutContent.benefits.map((b, i) => (
              <Input
                key={i}
                value={b}
                onChange={(e) => {
                  const next = [...aboutContent.benefits];
                  next[i] = e.target.value;
                  setAboutContent((c) => ({ ...c, benefits: next }));
                }}
                placeholder={`Benefit #${i + 1}`}
                data-testid={`about-benefit-${i}`}
              />
            ))}
          </div>
        </div>

        <div className="mb-3">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Image</label>
          <div className="flex gap-2 mt-1">
            <Input
              value={aboutContent.image_url}
              onChange={(e) => setAboutContent((c) => ({ ...c, image_url: e.target.value }))}
              placeholder="/api/files/… or https://…"
              className="flex-1"
            />
            <Button type="button" variant="outline" onClick={() => setAboutMediaOpen(true)} data-testid="about-image-library-btn">
              <Library size={14} className="mr-1" />Library
            </Button>
          </div>
          <Input
            value={aboutContent.image_alt}
            onChange={(e) => setAboutContent((c) => ({ ...c, image_alt: e.target.value }))}
            placeholder="Image alt text (SEO)"
            className="mt-2"
          />
          {aboutContent.image_url && (
            <img
              src={aboutContent.image_url.startsWith('http') ? aboutContent.image_url : `${API}${aboutContent.image_url}`}
              alt=""
              className="mt-2 h-40 w-full object-cover rounded-lg"
              onError={(e) => (e.target.style.display = 'none')}
            />
          )}
        </div>

        <Button
          type="button"
          onClick={saveAboutContent}
          disabled={aboutSaving}
          className="mt-2 bg-sky-500 hover:bg-sky-400 w-full"
          data-testid="about-save-btn"
        >
          {aboutSaving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <BookOpen size={14} className="mr-2" />}
          {aboutSaving ? 'Saving…' : 'Save Discover Paisley block'}
        </Button>
      </div>

      <MediaPicker
        open={aboutMediaOpen}
        onClose={() => setAboutMediaOpen(false)}
        title="Pick the Discover Paisley image"
        onSelect={(url) => setAboutContent((c) => ({ ...c, image_url: url }))}
      />
    </div>
  );
};

export default SiteSettingsPage;
